/**
 * Color extraction utility for collection images (Browser-compatible)
 * Extracts dominant colors from images similar to Apple Music/iTunes
 */

(function() {
  'use strict';

/**
 * Extract dominant colors from an image URL
 */
async function extractColorsFromImage(imageUrl, sampleSize = 50) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Scale down image for faster processing
        const scale = Math.min(sampleSize / img.width, sampleSize / img.height);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Sample pixels
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        const colorCounts = new Map();

        // Sample pixels (skip every 16th pixel for performance)
        for (let i = 0; i < pixels.length; i += 16) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];

          // Skip transparent pixels
          if (a < 128) continue;

          // Quantize colors to reduce noise
          const quantized = quantizeColor(r, g, b);
          const key = `${quantized.r},${quantized.g},${quantized.b}`;
          colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
        }

        // Sort colors by frequency
        const sortedColors = Array.from(colorCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([color]) => {
            const [r, g, b] = color.split(',').map(Number);
            return { r, g, b };
          });

        // Extract dominant colors
        const primary = sortedColors[0] || { r: 100, g: 100, b: 100 };
        const secondary = sortedColors[1] || primary;
        const accent = sortedColors[2] || secondary;

        // Calculate text color based on luminance
        const textColor = getContrastColor(primary);

        resolve({
          primary: `rgb(${primary.r}, ${primary.g}, ${primary.b})`,
          secondary: `rgb(${secondary.r}, ${secondary.g}, ${secondary.b})`,
          accent: `rgb(${accent.r}, ${accent.g}, ${accent.b})`,
          text: textColor,
          background: `rgb(${primary.r}, ${primary.g}, ${primary.b})`,
          overlay: `rgba(${secondary.r}, ${secondary.g}, ${secondary.b}, 0.85)`,
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = imageUrl;
  });
}

function quantizeColor(r, g, b, levels = 8) {
  const step = 256 / levels;
  return {
    r: Math.floor(r / step) * step,
    g: Math.floor(g / step) * step,
    b: Math.floor(b / step) * step,
  };
}

function getContrastColor(rgb) {
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

function createGradient(colors, direction = 'vertical') {
  const deg = direction === 'horizontal' ? '90deg' : direction === 'radial' ? 'circle' : '180deg';
  
  if (direction === 'radial') {
    return `radial-gradient(circle at center, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.accent} 100%)`;
  }
  
  return `linear-gradient(${deg}, ${colors.primary} 0%, ${colors.secondary} 50%, ${colors.accent} 100%)`;
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.ColorExtraction = {
    extractColorsFromImage,
    createGradient,
  };
}

})(); // End IIFE















